import { useSelector } from 'react-redux';
import { colors, Icon, StandAloneLink, Surface, Tooltip, Txt } from '@vst/beam';
import Info from '@vst/beam-icons/icons/Info';
import { useTranslation } from 'react-i18next';

import { selectLocale } from '@app/redux';
import { formatPrice, selectChangePlan, selectChosenPlan } from '@app/redux/changePlan';

import MonthlyProductCharge from '../MonthlyProductCharge';

import styles from './styles.module.scss';

type MonthlyChargesProps = {
  handleBackNavigation: (e: React.MouseEvent<HTMLElement>) => void;
};

const TWENTY_FOUR_MONTHS = '24';

const MonthlyCharges = ({ handleBackNavigation }: MonthlyChargesProps) => {
  const { t } = useTranslation('ChangePlan', {
    keyPrefix: 'orderReview.cartDetails',
  });

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    cart: {
      cartItems: { plan, bundledAddons },
      prices: {
        monthly: { totalWithoutPromos },
      },
    },
  } = useSelector(selectChangePlan);

  const selectedPlan = useSelector(selectChosenPlan);
  const { price, priceCurrency, characteristics } = selectedPlan;
  const formattedPlanPrice = formatPrice(userLocale, price, priceCurrency);
  const formattedTotal = formatPrice(userLocale, totalWithoutPromos, priceCurrency);

  const leaseFeePrice = plan?.leaseFeePrice;
  const contractTerms = plan?.contractTerms;
  const formattedLeaseFee = formatPrice(userLocale, plan?.leaseFeePrice, priceCurrency);

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleBackNavigation(e);
  };

  const contractPeriodTitle = contractTerms?.includes(TWENTY_FOUR_MONTHS)
    ? t('cartSummary.24monthContract')
    : t('cartSummary.MonthToMonth');

  const contractPeriodTooltip = contractTerms?.includes(TWENTY_FOUR_MONTHS)
    ? t('cartSummary.24monthsTooltip')
    : t('cartSummary.MonthToMonthTooltip');

  return (
    <Surface
      data-cy="internet-summary"
      className={styles['internet-summary-container']}
      variant="secondary"
      radius="8px"
      p="16px"
    >
      <div data-cy="internet-summary-title" className={styles['internetSummaryDataInfo']}>
        <Txt variant="heading6">{t('pricingDetails.title')}</Txt>

        <StandAloneLink
          data-cy="change-plan-button"
          onClick={onClick}
          href=""
          underline={false}
          linkSize="small"
          style={{ textDecoration: 'none' }}
        >
          {t('changeButton')}
        </StandAloneLink>
      </div>

      <div className={styles['cart-items-container']}>
        <div>
          <MonthlyProductCharge
            id="internet-summary-pricing"
            productName={characteristics.offerName}
            formattedPrice={formattedPlanPrice}
          />

          {contractTerms && (
            <div className={styles['contract']} data-cy="internet-product-contract">
              <Txt variant="tinyItalic">{contractPeriodTitle}</Txt>

              <Tooltip
                data-cy="internet-product-tooltip-text"
                bodyText={contractPeriodTooltip}
                variant="description"
                beakPosition="center"
              >
                <Icon icon={Info} color="gray_600" size="16px" />
              </Tooltip>
            </div>
          )}
        </div>

        <>
          {bundledAddons && bundledAddons.length > 0
            ? bundledAddons.map((addon) => (
                <MonthlyProductCharge
                  key={`bundle-addon-${addon.name}`}
                  id={`bundle-addon-${addon.name}`}
                  productName={addon.name}
                  formattedPrice={Number(addon.price) === 0 ? t('free') : addon.price}
                  priceColor="success"
                />
              ))
            : null}
        </>

        {leaseFeePrice && (
          <MonthlyProductCharge
            id="lease-fee-item"
            productName={t('cartSummary.MonthlyLeaseFee')}
            formattedPrice={formattedLeaseFee}
          />
        )}
      </div>

      <hr />

      <div data-cy="total" className={styles['internetSummaryDataInfo']}>
        <Txt variant="paragraphBold" data-cy="total-value" style={{ margin: '0 0 0 auto' }}>
          {formattedTotal}
          <Txt color="subtle" component="span">
            *
          </Txt>
        </Txt>
      </div>
    </Surface>
  );
};

export default MonthlyCharges;
